import React, { useState, useRef, useEffect, useCallback } from "react";
import "./styles/PopoverField.css";

/**
 * PopoverField - A trigger button that opens a small panel underneath.
 *
 * Shared by DateRangeFilter and NumberRangeFilter so range filters open
 * the same way FilterDropdown does: on click, closable with Escape, and
 * reachable from the keyboard. They were previously :hover-only.
 *
 * @param {Object} props
 * @param {string} props.label - Trigger text
 * @param {boolean} props.active - Render as an applied filter
 * @param {string} props.align - 'left' (default) or 'right'
 * @param {React.ReactNode} props.children - Panel content
 */
const PopoverField = ({ label, active = false, align = "left", children }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);

  const close = useCallback((refocus) => {
    setOpen(false);
    if (refocus && triggerRef.current) triggerRef.current.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        close(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close(true);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  return (
    <div
      ref={rootRef}
      className={[
        "dsPopoverField",
        open ? "isOpen" : "",
        active ? "isActive" : "",
        align === "right" ? "alignRight" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        ref={triggerRef}
        type="button"
        className="dsSelectTrigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="dsSelectLabel">{label}</span>
        <span className="dsSelectChevron" aria-hidden="true" />
      </button>

      {open && <div className="dsPopoverPanel">{children}</div>}
    </div>
  );
};

export default PopoverField;
